/**
 * 診断結果を「端末画面に出す文字列」に整形する。
 *
 * 計算は diagnostics.ts が担当し、ここは見せ方だけを担当する。
 * 出力形式は実機に寄せつつ、初心者向けの注釈を 1 行足す。
 */

import type { DnsLookupResult, NetworkWorld, PingResult, TracerouteHop } from '../types/network';
import { pingStats, type IpConfigResult } from './diagnostics';

export interface TerminalLine {
  text: string;
  tone: 'normal' | 'ok' | 'warn' | 'error' | 'hint';
}

const line = (text: string, tone: TerminalLine['tone'] = 'normal'): TerminalLine => ({ text, tone });

/** ping 失敗理由を、初心者にも分かる日本語のヒントにする */
export function pingFailureHint(reason: PingResult['failureReason']): string {
  switch (reason) {
    case 'no-ip':
      return 'ヒント: 自分の PC が IP アドレスを持っていません。まず ipconfig で自分の設定を確認しましょう。';
    case 'dns-failed':
      return 'ヒント: 名前を IP アドレスに変換できませんでした。nslookup で DNS を調べてみましょう。';
    case 'link-down':
      return 'ヒント: 途中のケーブル（リンク）が切れています。物理的な接続から疑いましょう。';
    case 'no-route':
      return 'ヒント: 相手までの道がありません。サブネットやデフォルトゲートウェイの設定を確認しましょう。';
    case 'blocked':
      return 'ヒント: 途中のファイアウォールで止められています。ルールを確認しましょう。';
    case 'host-down':
      return 'ヒント: 相手の機器の電源が入っていないようです。';
    case 'quarantined':
      return 'ヒント: どちらかの機器がネットワークから隔離されています。';
    default:
      return '';
  }
}

export function formatPing(world: NetworkWorld, result: PingResult): TerminalLine[] {
  const out: TerminalLine[] = [];
  const targetLabel = result.resolvedIp
    ? `${result.target} [${result.resolvedIp}]`
    : result.target;
  out.push(line(`${targetLabel} に ping を送信しています:`));

  if (result.failureReason !== null) {
    const message =
      result.failureReason === 'dns-failed'
        ? `ping 要求ではホスト ${result.target} が見つかりません。名前を確認してもう一度実行してください。`
        : '要求がタイムアウトしました。';
    for (let i = 0; i < result.replies.length; i++) {
      out.push(line(message, 'error'));
      if (result.failureReason === 'dns-failed') break;
    }
  } else {
    for (const rtt of result.replies) {
      if (rtt === null) {
        out.push(line('要求がタイムアウトしました。', 'error'));
      } else {
        out.push(
          line(`${result.resolvedIp} からの応答: バイト数 =32 時間 =${rtt}ms TTL=128`, 'ok'),
        );
      }
    }
  }

  const stats = pingStats(result);
  out.push(line(''));
  out.push(line(`${result.resolvedIp ?? result.target} の ping 統計:`));
  out.push(
    line(
      `    パケット数: 送信 = ${stats.sent}、受信 = ${stats.received}、損失 = ${stats.lost} (${stats.lossPercent}% の損失)`,
      stats.lost > 0 ? 'warn' : 'ok',
    ),
  );
  if (stats.avgMs !== null) {
    out.push(line('ラウンド トリップの概算時間 (ミリ秒):'));
    out.push(line(`    最小 = ${stats.minMs}ms、最大 = ${stats.maxMs}ms、平均 = ${stats.avgMs}ms`));
  }

  const hint = pingFailureHint(result.failureReason);
  if (hint) {
    out.push(line(''));
    out.push(line(hint, 'hint'));
  }
  void world;
  return out;
}

export function formatIpconfig(config: IpConfigResult | null): TerminalLine[] {
  if (!config) return [line('対象の機器が見つかりません。', 'error')];

  const out: TerminalLine[] = [];
  out.push(line('MiraiOS IP 構成'));
  out.push(line(''));
  out.push(line(`イーサネット アダプター (${config.label}):`));
  out.push(line(''));
  out.push(
    line(
      `   メディアの状態. . . . . . . . . . . : ${config.mediaConnected ? '接続済み' : 'メディアは接続されていません'}`,
      config.mediaConnected ? 'ok' : 'error',
    ),
  );
  out.push(line(`   物理アドレス. . . . . . . . . . . : ${config.macAddress}`));
  out.push(line(`   DHCP 有効 . . . . . . . . . . . . : ${config.dhcp ? 'はい' : 'いいえ'}`));
  out.push(
    line(
      `   IPv4 アドレス . . . . . . . . . . : ${config.address ?? '(未設定)'}${config.apipa ? '（自動構成）' : ''}`,
      config.apipa || !config.address ? 'warn' : 'ok',
    ),
  );
  out.push(line(`   サブネット マスク . . . . . . . . : ${config.subnetMask}`));
  out.push(line(`   デフォルト ゲートウェイ . . . . . : ${config.gateway ?? '(なし)'}`));
  out.push(
    line(
      `   DNS サーバー. . . . . . . . . . . : ${config.dnsServers.length ? config.dnsServers.join(', ') : '(なし)'}`,
    ),
  );
  if (config.cidr) {
    out.push(line(`   ネットワーク アドレス . . . . . . : ${config.cidr}`));
  }
  for (const note of config.notes) {
    out.push(line(''));
    out.push(line(`※ ${note}`, 'warn'));
  }
  return out;
}

export function formatNslookup(result: DnsLookupResult): TerminalLine[] {
  const out: TerminalLine[] = [];
  out.push(line(`サーバー:  ${result.serverIp ?? '(未設定)'}`));
  out.push(line(''));

  switch (result.failureReason) {
    case null:
      out.push(line('権限のない回答:'));
      out.push(line(`名前:     ${result.hostname}`));
      out.push(line(`Address:  ${result.address}`, 'ok'));
      break;
    case 'no-server':
      out.push(line('DNS サーバーが設定されていません。', 'error'));
      out.push(line('ヒント: ipconfig で DNS サーバーの設定を確認しましょう。', 'hint'));
      break;
    case 'server-unreachable':
      out.push(
        line(`DNS request timed out. ${result.serverIp ?? ''} に到達できません。`.trim(), 'error'),
      );
      out.push(
        line('ヒント: DNS サーバーそのものに届いていません。まずサーバーへ ping してみましょう。', 'hint'),
      );
      break;
    case 'nxdomain':
      out.push(line(`*** ${result.serverIp} が ${result.hostname} を見つけられません: NXDOMAIN`, 'error'));
      out.push(
        line('ヒント: DNS サーバーには届いていますが、その名前の登録がありません。', 'hint'),
      );
      break;
  }
  return out;
}

export function formatTraceroute(target: string, hops: TracerouteHop[]): TerminalLine[] {
  const out: TerminalLine[] = [];
  out.push(line(`${target} へのルートをトレースしています（最大 15 ホップ）:`));
  out.push(line(''));
  for (const hop of hops) {
    if (hop.rttMs === null) {
      out.push(line(`  ${String(hop.hop).padStart(2, ' ')}     *        *        *     要求がタイムアウトしました。`, 'error'));
    } else {
      const rtt = `${hop.rttMs} ms`.padStart(8, ' ');
      out.push(
        line(
          `  ${String(hop.hop).padStart(2, ' ')} ${rtt}   ${hop.ip ?? ''}  ${hop.deviceLabel ?? ''}`.trimEnd(),
          'ok',
        ),
      );
    }
  }
  out.push(line(''));
  const failed = hops.some((h) => h.rttMs === null);
  out.push(
    failed
      ? line('トレースは途中で止まりました。最後に応答した機器の「先」で問題が起きています。', 'hint')
      : line('トレースを完了しました。', 'ok'),
  );
  return out;
}
